/**
 * Normalizes raw API/mock/scraped records into canonical PublicVehicleListing shape (Milestone 2).
 */
import { getPrimaryListingImageUrl, getListingImageCount } from "../../clientViewRuntime/utils/listingImageDisplay";
import { resolveFairPriceLabel } from "../fairPriceDisplay";
import { resolveRiskLevel } from "../vehicleRiskDisplay";
import { isVerifiedDealer } from "../dealerVerificationDisplay";
import {
  AVAILABILITY_STATUS,
  DEALER_VERIFICATION_STATUS,
  LISTING_BODY_TYPE,
  LISTING_FAIR_PRICE_LABEL,
  LISTING_FUEL_TYPE,
  LISTING_PLACEHOLDER_IMAGE,
  LISTING_RISK_LEVEL,
  LISTING_STATUS,
  LISTING_TRANSMISSION,
} from "../../constants/automapListingConstants";
import { formatLocationLabel, formatVehicleTitle } from "./listingFormatters";
import { dedupeVehicleIdentityParts } from "./canonicalVehicleTitle";
import { getListingPublicStatus } from "./listingVisibility";

function firstDefined(...values) {
  for (const v of values) {
    if (v != null && v !== "") return v;
  }
  return null;
}

function pickNumber(...values) {
  for (const v of values) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

function pickId(raw) {
  return firstDefined(raw?.id, raw?.vehicleListingId, raw?.listingId, raw?.externalListingId);
}

function pickSlug(raw, id) {
  return firstDefined(raw?.slug, raw?.listingSlug, raw?.urlSlug, raw?.seoSlug) || (id ? String(id) : null);
}

function pickPrice(raw) {
  return pickNumber(raw?.priceCop, raw?.currentPriceCop, raw?.price_cop, raw?.listPrice, raw?.price);
}

function pickMileage(raw) {
  return pickNumber(raw?.mileageKm, raw?.mileage_km, raw?.mileage, raw?.kilometers, raw?.km);
}

function pickVehicleYear(raw) {
  return pickNumber(raw?.year, raw?.modelYear, raw?.model_year);
}

function pickVehicleMake(raw) {
  return firstDefined(
    raw?.makeNormalized,
    raw?.make_normalized,
    raw?.make,
    raw?.makeRaw,
    raw?.make_raw,
    raw?.brand
  );
}

function pickVehicleModel(raw) {
  return firstDefined(
    raw?.modelNormalized,
    raw?.model_normalized,
    raw?.model,
    raw?.modelRaw,
    raw?.model_raw
  );
}

function pickVehicleTrim(raw) {
  return firstDefined(
    raw?.trimNormalized,
    raw?.trim_normalized,
    raw?.trim,
    raw?.trimRaw,
    raw?.trim_raw,
    raw?.versionNormalized,
    raw?.version_normalized,
    raw?.version,
    raw?.versionRaw,
    raw?.version_raw
  );
}

function pickSourceTitle(raw) {
  return firstDefined(
    raw?.listing_title,
    raw?.listingTitle,
    raw?.sourceTitle,
    raw?.source_title,
    raw?.rawTitle,
    raw?.raw_title,
    raw?.dealerTitle,
    raw?.dealer_title,
    raw?.title
  );
}

function normalizeTransmission(value) {
  if (!value) return LISTING_TRANSMISSION.UNKNOWN;
  const v = String(value).toLowerCase();
  if (v.includes("auto") && !v.includes("manual")) return LISTING_TRANSMISSION.AUTOMATIC;
  if (v.includes("cvt")) return LISTING_TRANSMISSION.CVT;
  if (v.includes("secu") || v.includes("automated")) return LISTING_TRANSMISSION.AUTOMATED_MANUAL;
  if (v.includes("mec") || v.includes("manual")) return LISTING_TRANSMISSION.MANUAL;
  if (Object.values(LISTING_TRANSMISSION).includes(v)) return v;
  return LISTING_TRANSMISSION.UNKNOWN;
}

function normalizeFuelType(raw) {
  const value = firstDefined(raw?.fuelType, raw?.fuel, raw?.fuel_type);
  if (!value) return LISTING_FUEL_TYPE.UNKNOWN;
  const v = String(value).toLowerCase();
  if (
    v.includes("gasol") ||
    v.includes("gasolina") ||
    (v.includes("gas") && !v.includes("gasol"))
  ) {
    return LISTING_FUEL_TYPE.GASOLINE;
  }
  if (v.includes("diesel") || v.includes("diésel")) return LISTING_FUEL_TYPE.DIESEL;
  if (v.includes("híbr") || v.includes("hybrid")) return LISTING_FUEL_TYPE.HYBRID;
  if (v.includes("eléct") || v.includes("electric")) return LISTING_FUEL_TYPE.ELECTRIC;
  if (Object.values(LISTING_FUEL_TYPE).includes(v)) return v;
  return LISTING_FUEL_TYPE.UNKNOWN;
}

function normalizeBodyType(raw) {
  const value = firstDefined(raw?.bodyType, raw?.body_type, raw?.body);
  if (!value) return LISTING_BODY_TYPE.UNKNOWN;
  const v = String(value).toLowerCase();
  if (v.includes("suv") || v.includes("camioneta") || v.includes("campero")) return LISTING_BODY_TYPE.SUV;
  if (
    v.includes("sedán") ||
    v.includes("sedan") ||
    v.includes("automovil") ||
    v.includes("automóvil")
  ) {
    return LISTING_BODY_TYPE.SEDAN;
  }
  if (v.includes("hatch") || v.includes("compact")) return LISTING_BODY_TYPE.HATCHBACK;
  if (v.includes("pickup") || v.includes("pick-up") || v.includes("pickups")) {
    return LISTING_BODY_TYPE.PICKUP;
  }
  if (v.includes("van") || v.includes("furgon")) return LISTING_BODY_TYPE.VAN;
  if (v.includes("coupe") || v.includes("coupé")) return LISTING_BODY_TYPE.COUPE;
  if (v.includes("wagon") || v.includes("station")) return LISTING_BODY_TYPE.WAGON;
  if (Object.values(LISTING_BODY_TYPE).includes(v)) return v;
  return LISTING_BODY_TYPE.OTHER;
}

function normalizeListingStatus(raw) {
  const value = firstDefined(raw?.status, raw?.listingStatus, raw?.lifecycleStatus);
  if (!value) return LISTING_STATUS.UNKNOWN;
  const v = String(value).toLowerCase();
  if (Object.values(LISTING_STATUS).includes(v)) return v;
  if (v === "inactive") return LISTING_STATUS.PAUSED;
  return LISTING_STATUS.UNKNOWN;
}

function normalizeAvailabilityStatus(raw) {
  const value = firstDefined(raw?.availabilityStatus, raw?.availability_status, raw?.availability);
  if (!value) return AVAILABILITY_STATUS.UNKNOWN;
  const v = String(value).toLowerCase();
  if (Object.values(AVAILABILITY_STATUS).includes(v)) return v;
  if (v.includes("pending")) return AVAILABILITY_STATUS.PENDING_CONFIRMATION;
  return AVAILABILITY_STATUS.UNKNOWN;
}

function normalizeDealerVerification(raw) {
  const value = firstDefined(
    raw?.dealerVerificationStatus,
    raw?.verificationStatus,
    raw?.dealerVerified === true ? DEALER_VERIFICATION_STATUS.VERIFIED : null
  );
  if (!value && raw?.dealerVerified === false) return DEALER_VERIFICATION_STATUS.UNVERIFIED;
  if (!value) return DEALER_VERIFICATION_STATUS.UNKNOWN;
  const v = String(value).toLowerCase();
  if (Object.values(DEALER_VERIFICATION_STATUS).includes(v)) return v;
  if (v === "verified") return DEALER_VERIFICATION_STATUS.VERIFIED;
  return DEALER_VERIFICATION_STATUS.UNKNOWN;
}

function normalizeRiskLevel(raw) {
  const explicit = firstDefined(raw?.riskLevel, raw?.vehicleRiskLevel);
  if (explicit) {
    const v = String(explicit).toLowerCase();
    if (v === "bajo") return LISTING_RISK_LEVEL.LOW;
    if (Object.values(LISTING_RISK_LEVEL).includes(v)) return v;
  }
  return resolveRiskLevel(raw);
}

function normalizeFairPriceLabel(raw, merged) {
  const explicit = raw?.fairPriceLabel;
  if (explicit && Object.values(LISTING_FAIR_PRICE_LABEL).includes(explicit)) return explicit;
  const resolved = resolveFairPriceLabel(merged);
  return resolved || LISTING_FAIR_PRICE_LABEL.NOT_ENOUGH_DATA;
}

function pickImages(raw) {
  const fromArray = [];
  const candidates = raw?.images || raw?.photos || raw?.media?.images;
  if (Array.isArray(candidates)) {
    candidates.forEach((item) => {
      if (typeof item === "string" && item.trim()) fromArray.push(item.trim());
      else if (item?.url?.trim()) fromArray.push(item.url.trim());
      else if (item?.src?.trim()) fromArray.push(item.src.trim());
    });
  }
  if (raw?.imageUrl?.trim()) fromArray.unshift(raw.imageUrl.trim());
  if (raw?.primaryImageUrl?.trim() && !fromArray.includes(raw.primaryImageUrl.trim())) {
    fromArray.unshift(raw.primaryImageUrl.trim());
  }
  const primary = getPrimaryListingImageUrl(raw) || fromArray[0] || null;
  const unique = [...new Set(fromArray.filter(Boolean))];
  if (primary && !unique.includes(primary)) unique.unshift(primary);
  return { images: unique.length ? unique : primary ? [primary] : [], primaryImageUrl: primary };
}

/**
 * @param {object|null|undefined} raw
 * @returns {import('./listingTypes').PublicVehicleListing|null}
 */
export function normalizeVehicleListing(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id = pickId(raw);
  if (!id) return null;

  const { images, primaryImageUrl } = pickImages(raw);
  const priceCop = pickPrice(raw);
  const mileageKm = pickMileage(raw);
  const dealerName = firstDefined(raw?.dealerName, raw?.sellerName, raw?.dealer_name, raw?.sourceName);
  const dealerId = firstDefined(raw?.dealerId, raw?.dealer_id, raw?.sellerId);
  const city = firstDefined(raw?.city, raw?.cityName, raw?.location?.city, raw?.location);
  const department = firstDefined(raw?.department, raw?.departmentName, raw?.location?.department);

  const partial = {
    id: String(id),
    slug: pickSlug(raw, id),
    sourceTitle: pickSourceTitle(raw),
    make: pickVehicleMake(raw),
    model: pickVehicleModel(raw),
    year: pickVehicleYear(raw),
    trim: pickVehicleTrim(raw),
    version: pickVehicleTrim(raw),
    makeNormalized: firstDefined(raw?.makeNormalized, raw?.make_normalized),
    makeRaw: firstDefined(raw?.makeRaw, raw?.make_raw),
    modelNormalized: firstDefined(raw?.modelNormalized, raw?.model_normalized),
    modelRaw: firstDefined(raw?.modelRaw, raw?.model_raw),
    trimNormalized: firstDefined(raw?.trimNormalized, raw?.trim_normalized),
    trimRaw: firstDefined(raw?.trimRaw, raw?.trim_raw),
    versionNormalized: firstDefined(raw?.versionNormalized, raw?.version_normalized),
    versionRaw: firstDefined(raw?.versionRaw, raw?.version_raw),
    priceCop,
    mileageKm,
    transmission: normalizeTransmission(raw?.transmission),
    fuelType: normalizeFuelType(raw),
    bodyType: normalizeBodyType(raw),
    color: firstDefined(raw?.color, raw?.colour),
    doors: pickNumber(raw?.doors),
    engine: firstDefined(raw?.engine, raw?.engineSize),
    drivetrain: firstDefined(raw?.drivetrain, raw?.driveType),
    plateLastDigit: firstDefined(raw?.plateLastDigit, raw?.plate_last_digit),
    city,
    department,
    neighborhood: firstDefined(raw?.neighborhood, raw?.barrio),
    latitude: pickNumber(raw?.latitude, raw?.lat),
    longitude: pickNumber(raw?.longitude, raw?.lng, raw?.lon),
    dealerId: dealerId ? String(dealerId) : null,
    dealerName,
    dealerSlug: firstDefined(raw?.dealerSlug, raw?.dealer_slug),
    dealerLogoUrl: firstDefined(raw?.dealerLogoUrl, raw?.dealer_logo_url),
    dealerVerified: Boolean(raw?.dealerVerified ?? isVerifiedDealer(raw)),
    dealerVerificationStatus: normalizeDealerVerification(raw),
    dealerCity: firstDefined(raw?.dealerCity, raw?.dealer_city),
    dealerWhatsapp: firstDefined(raw?.dealerWhatsapp, raw?.dealer_whatsapp, raw?.sellerPhone),
    dealerPhone: firstDefined(raw?.dealerPhone, raw?.dealer_phone, raw?.sellerPhone),
    dealerResponseTimeLabel: firstDefined(raw?.dealerResponseTimeLabel, raw?.dealerResponseMinutes != null ? `${raw.dealerResponseMinutes} min` : null),
    // TODO(DealerProfile Future): Map dealerStatus and dealerPublicProfileEnabled when API exposes stable fields.
    images,
    primaryImageUrl: primaryImageUrl || LISTING_PLACEHOLDER_IMAGE,
    imageCount: getListingImageCount(raw) || images.length,
    status: normalizeListingStatus(raw),
    availabilityStatus: normalizeAvailabilityStatus(raw),
    listingSource: firstDefined(raw?.listingSource, raw?.sourceName, raw?.source),
    sourceKey: firstDefined(raw?.sourceKey, raw?.source_key),
    sourceUrl: firstDefined(raw?.sourceUrl, raw?.url, raw?.source_url),
    attributionLabel: firstDefined(raw?.attributionLabel, raw?.attribution_label),
    attributionText: firstDefined(raw?.attributionText, raw?.attribution_text),
    attributionLinkText: firstDefined(raw?.attributionLinkText, raw?.attribution_link_text),
    attributionUrl: firstDefined(raw?.attributionUrl, raw?.attribution_url),
    attributionRequired: raw?.attributionRequired === true || raw?.attribution_required === true,
    displayBrandName: firstDefined(raw?.displayBrandName, raw?.display_brand_name),
    ownershipDisclaimer: firstDefined(raw?.ownershipDisclaimer, raw?.ownership_disclaimer),
    sourceListingId: firstDefined(raw?.sourceListingId, raw?.source_listing_id),
    externalListingId: firstDefined(raw?.externalListingId, raw?.externalId),
    lastConfirmedAt: firstDefined(raw?.lastConfirmedAt, raw?.last_confirmed_at),
    publishedAt: firstDefined(raw?.publishedAt, raw?.published_at),
    createdAt: firstDefined(raw?.createdAt, raw?.created_at),
    updatedAt: firstDefined(raw?.updatedAt, raw?.updated_at),
    fairPriceDeltaCop: pickNumber(raw?.fairPriceDeltaCop, raw?.priceDeltaCop),
    fairPriceDeltaPercent: pickNumber(raw?.fairPriceDeltaPercent, raw?.priceDeltaPercent),
    fairPriceConfidence: firstDefined(raw?.fairPriceConfidence, raw?.confidence),
    fairPriceSampleSize: pickNumber(raw?.fairPriceComparableCount, raw?.comparableCount, raw?.fairPriceSampleSize),
    fairPriceComparableCount: pickNumber(raw?.fairPriceComparableCount, raw?.comparableCount),
    comparableMinPriceCop: pickNumber(raw?.comparableMinPriceCop, raw?.fairPriceComparableMinCop, raw?.comparableMinCop),
    comparableMaxPriceCop: pickNumber(raw?.comparableMaxPriceCop, raw?.fairPriceComparableMaxCop, raw?.comparableMaxCop),
    comparableMedianPriceCop: pickNumber(
      raw?.comparableMedianPriceCop,
      raw?.estimatedMarketPriceCop,
      raw?.marketPriceCop
    ),
    marketPriceCop: pickNumber(raw?.marketPriceCop, raw?.estimatedMarketPriceCop),
    estimatedMarketPriceCop: pickNumber(raw?.estimatedMarketPriceCop, raw?.marketPriceCop),
    fairPriceSource: firstDefined(raw?.fairPriceSource, raw?.fair_price_source),
    fairPriceUpdatedAt: firstDefined(raw?.fairPriceUpdatedAt, raw?.fair_price_updated_at),
    riskFlags: raw?.riskFlags || raw?.buyerVisibleRiskIndicators || raw?.buyerVisibleIndicators || raw?.vehicleRiskFlags || [],
    vehicleRiskScore: pickNumber(raw?.vehicleRiskScore, raw?.vehicle_risk_score),
    trustSafetyCaseCount: pickNumber(raw?.trustSafetyCaseCount, raw?.trust_safety_case_count),
    trustSafetyReportOpen: Boolean(raw?.trustSafetyReportOpen ?? raw?.trust_safety_report_open),
    duplicateCandidate: Boolean(raw?.duplicateCandidate ?? raw?.duplicate_candidate),
    staleListingFlag: Boolean(raw?.staleListingFlag),
    qualityScore: pickNumber(raw?.qualityScore, raw?.dataQualityScore, raw?.bestValueScore),
    qualityFlags: raw?.qualityFlags || [],
    featuredPlacement: raw?.featuredPlacement || null,
    sponsoredLabel: firstDefined(raw?.sponsoredLabel, raw?.placementLabel),
    seoTitle: firstDefined(raw?.seoTitle),
    seoDescription: firstDefined(raw?.seoDescription),
    modelDescription: firstDefined(raw?.modelDescription),
    modelDescriptionLocales: raw?.modelDescriptionLocales || null,
    modelDescriptionKey: firstDefined(raw?.modelDescriptionKey, raw?.model_description_key),
    financingAvailable: Boolean(raw?.financingAvailable),
    hasIncompleteInfo: Boolean(raw?.hasIncompleteInfo),
    estimatedFairPriceCop: pickNumber(raw?.estimatedFairPriceCop),
    placementId: raw?.placementId || null,
    slotKey: raw?.slotKey || null,
    raw,
  };

  // Public vehicle market API rows expose listingStatus without legacy status/availability fields.
  const publicListingStatus = firstDefined(raw?.listingStatus, raw?.listing_status);
  if (publicListingStatus && partial.status === LISTING_STATUS.UNKNOWN) {
    partial.status = normalizeListingStatus({ listingStatus: publicListingStatus });
  }

  // Ingested market listings expose listingStatus without availability; treat as available when active.
  if (
    partial.status === LISTING_STATUS.ACTIVE &&
    partial.availabilityStatus === AVAILABILITY_STATUS.UNKNOWN
  ) {
    partial.availabilityStatus = AVAILABILITY_STATUS.AVAILABLE;
  }

  const identity = dedupeVehicleIdentityParts({
    year: partial.year,
    make: partial.make,
    model: partial.model,
    trim: partial.trim,
  });
  partial.make = identity.make || partial.make;
  partial.model = identity.model || partial.model;
  partial.trim = identity.trim;
  partial.version = identity.trim;

  partial.title = formatVehicleTitle(partial);
  partial.locationLabel = formatLocationLabel(partial);
  partial.fairPriceLabel = normalizeFairPriceLabel(raw, partial);
  partial.riskLevel = normalizeRiskLevel(raw);
  partial.publicStatus = getListingPublicStatus(partial);
  partial.primaryImageUrl = partial.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE;
  if (!partial.images.length) {
    partial.images = [partial.primaryImageUrl];
  }

  // Legacy aliases for components not yet fully migrated (TODO M3: narrow usage)
  partial.vehicleListingId = partial.id;
  partial.currentPriceCop = partial.priceCop;
  partial.sellerName = partial.dealerName;
  partial.sourceName =
    firstDefined(raw?.sourceName, raw?.displayBrandName, raw?.listingSource) || partial.dealerName;
  partial.fuelType = partial.fuelType === LISTING_FUEL_TYPE.UNKNOWN ? raw?.fuelType || raw?.fuel : partial.fuelType;

  return partial;
}

export function normalizeVehicleListings(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map(normalizeVehicleListing).filter(Boolean);
}

export function normalizeListingForCard(raw) {
  return normalizeVehicleListing(raw);
}

export function normalizeListingForDetail(raw) {
  return normalizeVehicleListing(raw);
}

/** Canonical transmission key for search filter matching (aligns with filter UI values). */
export function canonicalListingTransmission(value) {
  return normalizeTransmission(value);
}

/** Canonical body type key for search filter matching (aligns with filter UI values). */
export function canonicalListingBodyType(value) {
  return normalizeBodyType({ bodyType: value });
}

/** Canonical fuel type key for search filter matching (gas and gasoline are equivalent). */
export function canonicalListingFuelType(value) {
  const normalized = normalizeFuelType({ fuelType: value });
  if (normalized === LISTING_FUEL_TYPE.GAS) return LISTING_FUEL_TYPE.GASOLINE;
  return normalized;
}
